import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { IAppInstall } from '../Dashboard/offsiteCampaignCreator.reducer';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import { getPaymentMethods } from '../../redux/payment/action';
import { userPaymentMethodsSelector } from '../../redux/payment/selector';
import { IPaymentCard } from '../../types/global/generic.types';
import PaymentCard from '../../components/PaymentCard/PaymentCard';
import { SimpleAddCardModal } from '../../components/Payment/SimpleAddCardModal';
import { useWalletFundOptions } from '../../hook/useWalletFundOptions';
import { defaultWalletFundProvider } from './walletFundRailUtils';
import { filterPaymentCardsForRail, type SubscriptionPaymentRail } from '../../utils/subscriptionRailCards';
import { ASW_S3_LINK } from '../../configurations/api/url';
import { showToastMessage } from '../../utils/AppUtils';

import { CPI_CENTS_BY_TIER, formatCpaCents, normalizeCircuulCpaTier } from '../../utils/circuulCpaRates';

function resolveMediaUrl(keyOrUrl?: string | null): string | null {
    if (!keyOrUrl) return null;
    if (/^https?:\/\//i.test(keyOrUrl) || keyOrUrl.startsWith('blob:') || keyOrUrl.startsWith('data:')) {
        return keyOrUrl;
    }
    return `${ASW_S3_LINK}${keyOrUrl.replace(/^\//, '')}`;
}

export interface AppInstallCampaignToggleProps {
    value: IAppInstall;
    onChange: (next: IAppInstall) => void;
    /** Local file for create/edit upload flow (saga persists to app_icon_url). */
    onAppIconFile?: (file: File | null) => void;
    appIconPreviewOverride?: string | null;
    /** Campaign influencer tier — drives CPI copy. */
    influencerTier?: string;
    fieldSx?: Record<string, unknown>;
    labelSx?: Record<string, unknown>;
}

export function AppInstallCampaignToggle({
    value,
    onChange,
    onAppIconFile,
    appIconPreviewOverride,
    influencerTier,
    fieldSx,
    labelSx,
}: AppInstallCampaignToggleProps) {
    const dispatch = useDispatch();
    const paymentMethods = useSelector(userPaymentMethodsSelector);
    const { fundOptions } = useWalletFundOptions();
    const iconInputRef = useRef<HTMLInputElement>(null);
    const [localIconPreview, setLocalIconPreview] = useState<string | null>(null);

    useEffect(() => {
        dispatch(getPaymentMethods());
    }, [dispatch]);

    const set = (key: keyof IAppInstall, v: string | boolean) => onChange({ ...value, [key]: v });

    const kind = value.kind ?? 'app_install';
    const isWebVisit = kind === 'web_visit';
    const cpi = CPI_CENTS_BY_TIER[normalizeCircuulCpaTier(influencerTier)];

    const rail: SubscriptionPaymentRail = useMemo(() => {
        const provider = defaultWalletFundProvider(fundOptions);
        if (provider === 'paystack' || provider === 'flutterwave') return provider;
        return 'stripe';
    }, [fundOptions]);

    const railCards = useMemo(() => filterPaymentCardsForRail(paymentMethods, rail), [paymentMethods, rail]);

    const selectedCard = useMemo(() => {
        const id = value.payment_method_id;
        if (!id) return null;
        return railCards.find(c => c._id === id || c.payment_method === id) || null;
    }, [railCards, value.payment_method_id]);

    const handleKindChange = (_: React.MouseEvent<HTMLElement>, next: 'app_install' | 'web_visit' | null) => {
        if (!next) return;
        onChange({ ...value, kind: next });
    };

    const handleAutoReload = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (!checked) {
            onChange({ ...value, auto_reload_enabled: false });
            return;
        }
        const keepId = selectedCard?._id || value.payment_method_id;
        onChange({
            ...value,
            auto_reload_enabled: true,
            ...(keepId ? { payment_method_id: keepId } : {}),
        });
    };

    const selectCard = (card: IPaymentCard) => {
        onChange({ ...value, payment_method_id: card._id, auto_reload_enabled: true });
    };

    const sharedFieldSx = fieldSx ?? {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: '#fff',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        },
    };

    const inputSx = {
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 14,
        color: '#fff',
    };

    const resolvedLabelSx = labelSx ?? {
        fontSize: 11,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.14em',
        color: 'rgba(255,255,255,0.55)',
        mb: 1,
    };

    const toggleBtnSx = {
        flex: 1,
        py: 1,
        gap: 1,
        textTransform: 'none' as const,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
        border: `1px solid rgba(255,255,255,0.1) !important`,
        borderRadius: '8px !important',
        '&.Mui-selected': {
            bgcolor: `${BRAND_LUXE_PRIMARY}18`,
            color: BRAND_LUXE_PRIMARY,
            border: `1px solid ${BRAND_LUXE_PRIMARY}55 !important`,
        },
        '&.Mui-selected:hover': { bgcolor: `${BRAND_LUXE_PRIMARY}22` },
    };

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                border: value.enabled ? `1px solid ${BRAND_LUXE_PRIMARY}55` : `1px solid transparent`,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: '#fff', letterSpacing: 0.2 }}>
                        App / Web install campaign
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.75,
                            fontSize: 14,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.72)',
                            lineHeight: 1.55,
                        }}>
                        Creators will drive installs or visits. Choose your campaign type below.
                    </Typography>
                </Box>
                <Switch
                    size="small"
                    checked={value.enabled}
                    onChange={(_, v) => onChange({ ...value, enabled: v })}
                    inputProps={{ 'aria-label': 'App install campaign' }}
                    sx={{
                        flexShrink: 0,
                        mt: 0.25,
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: BRAND_LUXE_SECONDARY,
                            '& + .MuiSwitch-track': { backgroundColor: 'rgba(0,108,80,0.55)', opacity: 1 },
                        },
                        '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                    }}
                />
            </Box>

            {value.enabled && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ToggleButtonGroup
                        value={kind}
                        exclusive
                        onChange={handleKindChange}
                        sx={{ display: 'flex', gap: 1, '& .MuiToggleButtonGroup-grouped': { mx: 0 } }}>
                        <ToggleButton value="app_install" sx={toggleBtnSx}>
                            <PhoneAndroidOutlinedIcon sx={{ fontSize: 16 }} />
                            App install
                        </ToggleButton>
                        <ToggleButton value="web_visit" sx={toggleBtnSx}>
                            <LanguageOutlinedIcon sx={{ fontSize: 16 }} />
                            Web visit
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Box
                        sx={{
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                        <Typography sx={{ ...resolvedLabelSx, mb: 0.5 }}>Cost per result (this tier)</Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: 'rgba(255,255,255,0.78)',
                                lineHeight: 1.5,
                            }}>
                            {formatCpaCents(cpi.unsubscribed)} if the creator is not subscribed ·{' '}
                            {formatCpaCents(cpi.subscribed)} if subscribed. Locked when a creator is approved.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={resolvedLabelSx}>App display name</Typography>
                        <TextField
                            fullWidth
                            placeholder="Meeteor"
                            value={value.app_display_name ?? ''}
                            onChange={e => set('app_display_name', e.target.value)}
                            sx={{ ...sharedFieldSx, '& .MuiInputBase-input': inputSx }}
                            helperText="Shown on the public invite link page (Get {name})"
                            FormHelperTextProps={{
                                sx: { color: 'rgba(255,255,255,0.45)', ml: 0 },
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography sx={resolvedLabelSx}>App icon</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                {appIconPreviewOverride || localIconPreview || resolveMediaUrl(value.app_icon_url) ? (
                                    <Box
                                        component="img"
                                        src={
                                            appIconPreviewOverride ||
                                            localIconPreview ||
                                            resolveMediaUrl(value.app_icon_url) ||
                                            undefined
                                        }
                                        alt="App icon"
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <AddPhotoAlternateOutlinedIcon
                                        sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 28 }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                                <input
                                    ref={iconInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    hidden
                                    onChange={e => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (!file) return;
                                        if (file.size > 5 * 1024 * 1024) {
                                            showToastMessage('Icon must be under 5MB', 'info');
                                            return;
                                        }
                                        const preview = URL.createObjectURL(file);
                                        setLocalIconPreview(prev => {
                                            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                                            return preview;
                                        });
                                        onAppIconFile?.(file);
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => iconInputRef.current?.click()}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        textTransform: 'none',
                                        color: '#fff',
                                        borderColor: 'rgba(255,255,255,0.2)',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Upload icon
                                </Button>
                                {(localIconPreview || value.app_icon_url) && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            if (localIconPreview?.startsWith('blob:')) {
                                                URL.revokeObjectURL(localIconPreview);
                                            }
                                            setLocalIconPreview(null);
                                            onAppIconFile?.(null);
                                            onChange({ ...value, app_icon_url: '' });
                                        }}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            textTransform: 'none',
                                            color: 'rgba(255,255,255,0.55)',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        Remove
                                    </Button>
                                )}
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'rgba(255,255,255,0.45)',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Square PNG/JPG, shown on the invite landing page.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {!isWebVisit && (
                        <>
                            <Box>
                                <Typography sx={resolvedLabelSx}>Android package name</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="com.brand.app"
                                    value={value.android_package ?? ''}
                                    onChange={e => set('android_package', e.target.value)}
                                    sx={{ ...sharedFieldSx, '& .MuiInputBase-input': inputSx }}
                                />
                            </Box>
                            <Box>
                                <Typography sx={resolvedLabelSx}>Google Play Store URL</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="https://play.google.com/store/apps/details?id=com.brand.app"
                                    value={value.android_store_url ?? ''}
                                    onChange={e => set('android_store_url', e.target.value)}
                                    sx={{ ...sharedFieldSx, '& .MuiInputBase-input': inputSx }}
                                />
                            </Box>
                            <Box>
                                <Typography sx={resolvedLabelSx}>Apple App Store URL</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="https://apps.apple.com/app/id123456"
                                    value={value.ios_store_url ?? ''}
                                    onChange={e => set('ios_store_url', e.target.value)}
                                    sx={{ ...sharedFieldSx, '& .MuiInputBase-input': inputSx }}
                                />
                            </Box>
                        </>
                    )}

                    {isWebVisit && (
                        <Box>
                            <Typography sx={resolvedLabelSx}>Website URL</Typography>
                            <TextField
                                fullWidth
                                placeholder="https://brand.com/landing"
                                value={value.web_fallback_url ?? ''}
                                onChange={e => set('web_fallback_url', e.target.value)}
                                sx={{ ...sharedFieldSx, '& .MuiInputBase-input': inputSx }}
                            />
                        </Box>
                    )}

                    {!isWebVisit && (
                        <Box>
                            <Typography sx={resolvedLabelSx}>Web fallback URL (optional)</Typography>
                            <TextField
                                fullWidth
                                placeholder="https://brand.com/download"
                                value={value.web_fallback_url ?? ''}
                                onChange={e => set('web_fallback_url', e.target.value)}
                                sx={{ ...sharedFieldSx, '& .MuiInputBase-input': inputSx }}
                            />
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 2,
                            pt: 0.5,
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                        }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: '#fff' }}>
                                Auto-reload budget
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 13,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: 'rgba(255,255,255,0.65)',
                                    lineHeight: 1.5,
                                }}>
                                When remaining float drops below 10% of the original budget, refill from wallet then
                                your card on file. Off = pause attributions and stop redirects when the float runs out.
                            </Typography>
                        </Box>
                        <Switch
                            size="small"
                            checked={Boolean(value.auto_reload_enabled)}
                            onChange={handleAutoReload}
                            inputProps={{ 'aria-label': 'Auto-reload budget' }}
                            sx={{
                                flexShrink: 0,
                                mt: 0.25,
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: BRAND_LUXE_SECONDARY,
                                    '& + .MuiSwitch-track': { backgroundColor: 'rgba(0,108,80,0.55)', opacity: 1 },
                                },
                                '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                            }}
                        />
                    </Box>

                    {value.auto_reload_enabled && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography sx={resolvedLabelSx}>Card on file</Typography>
                            {railCards.length === 0 ? (
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: 'rgba(255,201,101,0.9)',
                                    }}>
                                    Add an authorized card for auto-reload (payment rail for your region).
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {railCards.map(card => (
                                        <Box
                                            key={card._id}
                                            onClick={() => selectCard(card)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: 1.5,
                                                p: 1,
                                                border:
                                                    selectedCard?._id === card._id
                                                        ? `1px solid ${BRAND_LUXE_PRIMARY}`
                                                        : '1px solid rgba(255,255,255,0.1)',
                                                bgcolor:
                                                    selectedCard?._id === card._id
                                                        ? `${BRAND_LUXE_PRIMARY}14`
                                                        : 'transparent',
                                            }}>
                                            <PaymentCard
                                                card={card}
                                                isModalMode
                                                cardSize="28px"
                                                isSelected={selectedCard?._id === card._id}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <SimpleAddCardModal
                                variant="link"
                                title="Payment method"
                                onCardReady={card =>
                                    onChange({
                                        ...value,
                                        payment_method_id: card._id,
                                        auto_reload_enabled: true,
                                    })
                                }
                            />
                            {!selectedCard && (
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: '#ff8a80',
                                    }}>
                                    Select a card to enable auto-reload.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
